import {
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Flex,
  Heading,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Radio,
  RadioGroup,
  SlideFade,
  Stack,
  Text,
} from "@chakra-ui/react";
import { t } from "i18next";
import { useCallback, useMemo } from "react";
import { FaChevronDown } from "react-icons/fa";
import { EvaluationModel, evaluationModels, EvaluationModelType } from "../EvaluationModels";

interface ModelSelectorProps {
  selectedModelType?: EvaluationModelType;
  onSelectModel: (model: EvaluationModel) => void;
  disabledModels?: EvaluationModelType[];
}

export const ModelSelector = ({ selectedModelType, onSelectModel, disabledModels = [] }: ModelSelectorProps) => {
  const selectedModel = useMemo(() => {
    if (!selectedModelType) return undefined;
    return Object.values(evaluationModels).find((m) => m.type === selectedModelType);
  }, [selectedModelType]);

  const handleModelSelect = useCallback(
    (modelType: EvaluationModelType) => {
      const modelsList = Object.values(evaluationModels);
      const model = modelsList.find((m) => m.type === modelType);
      if (model) {
        onSelectModel(model);
      }
    },
    [onSelectModel]
  );

  return (
    <Box position="relative" minHeight="10">
      <Box
        position={selectedModel ? "absolute" : "relative"}
        width="full"
        top={0}
        left={0}
        pointerEvents={selectedModel ? "none" : "auto"}
      >
        <SlideFade in={!selectedModel} offsetY={-10} transition={{ enter: { duration: 0.3 }, exit: { duration: 0.2 } }}>
          <Card variant="outline">
            <CardHeader>
              <Heading size="md">{t("Select Evaluation Model")}</Heading>
            </CardHeader>
            <CardBody>
              <RadioGroup
                onChange={(value) => handleModelSelect(value as EvaluationModelType)}
                value={selectedModelType}
              >
                <Stack spacing={4}>
                  {Object.values(evaluationModels).map((model: EvaluationModel) => {
                    const isDisabled = disabledModels.includes(model.type);

                    return (
                      <Flex
                        key={model.type}
                        p={3}
                        borderWidth="1px"
                        borderRadius="md"
                        alignItems="center"
                        opacity={isDisabled ? 0.5 : 1}
                        cursor={isDisabled ? "not-allowed" : "pointer"}
                        onClick={() => {
                          if (!isDisabled) {
                            handleModelSelect(model.type);
                          }
                        }}
                        position="relative"
                        bg={selectedModelType === model.type ? "smPrimarySofter" : undefined}
                        borderColor={selectedModelType === model.type ? "smPrimary" : "smBorder"}
                      >
                        <Radio value={model.type} isDisabled={isDisabled} colorScheme="teal" opacity={0.8} mr={3} />
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          w={6}
                          h={6}
                          mr={3}
                          color={selectedModelType === model.type ? "smPrimary" : "smMuted"}
                        >
                          {model.getModelIcon()}
                        </Box>
                        <Box flex={1}>
                          <Text fontWeight="medium" color="smText">
                            {model.name()}
                          </Text>
                          <Text fontSize="sm" color="smMuted" mt={1}>
                            {model.description()}.
                          </Text>
                        </Box>
                      </Flex>
                    );
                  })}
                </Stack>
              </RadioGroup>
            </CardBody>
          </Card>
        </SlideFade>
      </Box>
      <Box
        position={selectedModel ? "relative" : "absolute"}
        width="100%"
        top={0}
        left={0}
        pointerEvents={selectedModel ? "auto" : "none"}
      >
        <SlideFade in={!!selectedModel} offsetY={10} transition={{ enter: { duration: 0.3 }, exit: { duration: 0.2 } }}>
          <Menu>
            <MenuButton
              as={Button}
              rightIcon={<FaChevronDown />}
              variant="outline"
              colorScheme="teal"
              size="md"
              width="full"
              justifyContent="space-between"
            >
              <Flex alignItems="center" gap={3}>
                <Box display="flex" alignItems="center" justifyContent="center" w={5} h={5} color="smPrimary">
                  {selectedModel?.getModelIcon()}
                </Box>
                <Text fontWeight="medium">{selectedModel?.name()}</Text>
              </Flex>
            </MenuButton>
            <MenuList>
              {Object.values(evaluationModels).map((model: EvaluationModel) => {
                const isDisabled = disabledModels.includes(model.type);
                const isSelected = model.type === selectedModelType;

                return (
                  <MenuItem
                    key={model.type}
                    isDisabled={isDisabled}
                    onClick={() => handleModelSelect(model.type)}
                    bg={isSelected ? "smPrimarySofter" : undefined}
                    _hover={isSelected ? { bg: "smPrimarySofter" } : undefined}
                  >
                    <Flex alignItems="center" gap={3} width="full">
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        w={5}
                        h={5}
                        color={isSelected ? "smPrimary" : "smMuted"}
                      >
                        {model.getModelIcon()}
                      </Box>
                      <Box flex={1}>
                        <Text fontWeight={isSelected ? "semibold" : "medium"}>{model.name()}</Text>
                        <Text fontSize="xs" color="smMuted">
                          {model.description()}
                        </Text>
                      </Box>
                    </Flex>
                  </MenuItem>
                );
              })}
            </MenuList>
          </Menu>
        </SlideFade>
      </Box>
    </Box>
  );
};
